import styled, {css, DefaultTheme} from 'styled-components'
import {BadgeProps} from '@components/ui/badge/Badge.tsx'

type StyledBadgeProps = {
    $shape: NonNullable<BadgeProps['shape']>
    $size: NonNullable<BadgeProps['size']>
    $variant: NonNullable<BadgeProps['variant']>
}

const makeBadgeBaseStyle = ({theme, $shape}: {theme: DefaultTheme; $shape: StyledBadgeProps['$shape']}) => css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    gap: ${theme.spacing}px;
    outline-offset: -1px;
    word-break: break-word;
    ${($shape == 'square' || $shape == 'circle') &&
    css`
        flex-shrink: 0;
    `}

    /* The .custom class is used to enable icon resizing from the component */
    & svg:not(.custom) {
        width: 15px;
        height: 15px;
    }
`

const makeBadgeSizeStyle = (theme: DefaultTheme) =>
    ({
        sm: {
            rectangle: css`
                padding: ${theme.spacing * 0.5}px ${theme.spacing * 2}px;
                ${theme.typography.textXs};
                border-radius: 6px;
                min-height: 22px;
            `,
            roundedRectangle: css`
                padding: ${theme.spacing * 0.5}px ${theme.spacing * 2}px;
                ${theme.typography.textXs};
                border-radius: 11px;
                min-height: 22px;
            `,
            square: css`
                width: 22px;
                height: 22px;
                border-radius: 6px;
            `,
            circle: css`
                width: 22px;
                height: 22px;
                border-radius: 11px;
            `
        },
        md: {
            rectangle: css`
                padding: ${theme.spacing * 0.5}px ${theme.spacing * 2.5}px;
                ${theme.typography.textSm};
                border-radius: 6px;
                min-height: 24px;
            `,
            roundedRectangle: css`
                padding: ${theme.spacing * 0.5}px ${theme.spacing * 2.5}px;
                ${theme.typography.textSm};
                border-radius: 12px;
                min-height: 24px;
            `,
            square: css`
                width: 24px;
                height: 24px;
                border-radius: 6px;
            `,
            circle: css`
                width: 24px;
                height: 24px;
                border-radius: 12px;
            `
        },
        lg: {
            rectangle: css`
                padding: ${theme.spacing}px ${theme.spacing * 3}px;
                ${theme.typography.textSm};
                border-radius: 6px;
                min-height: 28px;
            `,
            roundedRectangle: css`
                padding: ${theme.spacing}px ${theme.spacing * 2}px;
                ${theme.typography.textSm};
                border-radius: 14px;
                min-height: 28px;
            `,
            square: css`
                width: 28px;
                height: 28px;
                border-radius: 6px;
            `,
            circle: css`
                width: 28px;
                height: 28px;
                border-radius: 14px;
            `
        }
    }) as const satisfies Record<
        NonNullable<StyledBadgeProps['$size']>,
        Record<NonNullable<StyledBadgeProps['$shape']>, ReturnType<typeof css>>
    >

const makeBadgeVariantStyle = (theme: DefaultTheme) =>
    ({
        primary: css`
            color: ${theme.palette.primary['700']};
            background: ${theme.palette.primary['50']};
            outline: 1px solid ${theme.palette.primary['200']};
            & svg {
                fill: ${theme.palette.primary['500']};
            }
        `,
        neutral: css`
            color: ${theme.palette.neutral['700']};
            background: ${theme.palette.neutral['50']};
            outline: 1px solid ${theme.palette.neutral['200']};
            & svg {
                fill: ${theme.palette.neutral['500']};
            }
        `,
        success: css`
            color: ${theme.palette.green['800']};
            background: ${theme.palette.green['100']};
            outline: 1px solid ${theme.palette.green['200']};
            & svg {
                fill: ${theme.palette.green['500']};
            }
        `,
        danger: css`
            color: ${theme.palette.danger['800']};
            background: ${theme.palette.danger['100']};
            outline: 1px solid ${theme.palette.danger['200']};
            & svg {
                fill: ${theme.palette.danger['500']};
            }
        `,
        orange: css`
            color: ${theme.palette.orange['800']};
            background: ${theme.palette.orange['100']};
            outline: 1px solid ${theme.palette.orange['200']};
            & svg {
                fill: ${theme.palette.orange['500']};
            }
        `
    }) as const satisfies Record<NonNullable<StyledBadgeProps['$variant']>, ReturnType<typeof css>>

export const StyledBadge = styled.div<StyledBadgeProps & {disabled?: boolean}>(
    ({theme, ...props}) => css`
        ${makeBadgeSizeStyle(theme)[props.$size][props.$shape]};
        ${makeBadgeVariantStyle(theme)[props.$variant]};
        ${makeBadgeBaseStyle({theme, $shape: props.$shape})};
    `
)
